<template>
  <div
    class="public-container"
    :style="
      apiDown
        ? 'background-image: url(img/public/splash-static.png)'
        : 'background-image: url(img/public/splash.gif)'
    "
  >
    <error v-if="apiDown" message="Item Not Discoverable"></error>
    <div v-else>
      <loading v-if="loading"></loading>
      <div v-if="modalOpened">
        <md-dialog
          :md-active.sync="modalOpened"
          :md-close-on-esc="false"
          :md-click-outside-to-close="false"
        >
          <md-dialog-content>
            <valid-check-modal
              v-if="rfiderCaptchaResult"
              :data="rfiderCaptchaResult"
              @on-success="rfiderCaptchaSuccess"
              @on-fail="getRfiderCaptchaResult"
            ></valid-check-modal>
          </md-dialog-content>
        </md-dialog>
      </div>
      <div v-else>
        <result-component
          v-if="taskResult"
          :data="taskResult"
          :routerQuery="routerQuery"
        ></result-component>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import store from '@/store';
import { mapState } from 'vuex';
import { VueReCaptcha } from 'vue-recaptcha-v3';
import settings from '@/settings';
import Error from '@/components/generic/errorComponent.vue';
import Loading from '@/components/generic/loadingComponent.vue';
import ResultComponent from '@/components/code/resultComponent.vue';
import { captchaApi } from '@/api/http/api';
import ValidCheckModal from '@/components/recapture/validCheck.vue';
import { getCodePrefixes, getDefaultCodePrefix } from '@/utils';

let loaded = false;

export default {
  components: {
    Error,
    ResultComponent,
    Loading,
    ValidCheckModal,
  },

  data: () => ({
    loading: true,
    locationErrorDefaultMessage: "Oops, we couldn't access your location",
    showContinueWithoutLocation: false,
    triggered: false,
    rfiderCaptchaResult: null,
    modalOpened: false,
    routerQuery: null,
  }),

  computed: {
    ...mapState('code', ['taskResult', 'apiDown', 'recaptchaTest']),
  },

  watch: {
    taskResult: {
      immediate: true,
      handler(newVal) {
        this.loading = !newVal;
      },
    },
  },

  beforeRouteEnter(to, from, next) {
    if (!loaded) {
      loaded = true;
      Vue.use(VueReCaptcha, {
        siteKey: settings.recaptchaKey,
        loaderOptions: {
          useRecaptchaNet: true,
        },
      });
    }
    next((vm) => {
      vm.routerQuery = to.query;
      vm.recaptcha();
    });
  },

  beforeRouteUpdate(to, from, next) {
    this.recaptcha();
    next();
  },

  methods: {
    rfiderCaptchaSuccess() {
      this.invalidMessage = null;
      this.modalOpened = false;
      this.check();
    },

    getRfiderCaptchaResult() {
      captchaApi
        .getResult()
        .then((response) => {
          this.rfiderCaptchaResult = response.data;

          //first time doesn't return valid just return svg + source
          if (this.rfiderCaptchaResult.valid) {
            this.loading = true;
            this.check();
          } else {
            this.modalOpened = true;
          }
        })
        .catch((error) => {
          console.log(error);
        });
    },
    async recaptcha() {
      new Promise((resolve, reject) => {
        if (this.recaptchaTest) {
          reject();
        } else {
          this.$recaptchaLoaded()
            .then(() => {
              resolve(this.$recaptcha('web_view'));
            })
            .catch((err) => {
              reject(err);
            });
        }
      })
        .then((token) => {
          store.commit('code/SET_RECAPTCHA_TOKEN', token);
          this.check();
        })
        .catch(() => {
          this.getRfiderCaptchaResult();
        });

      // return;
      // // (optional) Wait until recaptcha has been loaded.
      // /** Here we need to perform a custom code to detect if the below code is loading correctly as it timeouts for Chinese users. */
      // if(await this.$recaptchaLoaded()){
      //   // Execute reCAPTCHA with action "login".
      //   await this.$recaptcha('web_view').then(token => {
      //     store.commit('code/SET_RECAPTCHA_TOKEN', token);
      //     this.check();
      //   });
      // }else{
      //    //If the above does not load we need to display custom captcha UI
      //   this.getRfiderCaptchaResult();
      // }
    },

    dispatch() {
      if (!this.triggered) {
        this.triggered = true;

        store
          .dispatch('code/runTask')
          .then((data) => {
            if (typeof data === 'string') {
              store.commit('code/SET_INIT_PATH', '');
              this.triggered = false;
              this.updateCode(data.replace('/', ''));
              this.check();
            }
          })
          .catch((err) => {
            if (
              err.response &&
              String(err.response.status).substr(0, 1) === '4'
            ) {
              const message =
                err.response.data.message || err.response.data.error;
              if (message) {
                this.locationErrorDefaultMessage = message;
              }
            }
          });
      }
    },
    updateCode(code) {
      const defaultPrefix = getDefaultCodePrefix();
      const validPrefix = getCodePrefixes();
      const groups = new RegExp(`^(${validPrefix.join('|')})?(.*)`)
        .exec(code)
        .slice(1, 3);

      if (!groups[0]) {
        code = defaultPrefix + code;
      }

      // Replace if code is coming with '-' as separator instead of '.'
      const codeParts = code.split('.');
      if (code.indexOf(defaultPrefix) === 0 && codeParts.length === 2) {
        const dataParts = codeParts[1].split('-');
        if (dataParts && dataParts.length > 2) {
          const checkSum = dataParts[dataParts.length - 1];
          const codeSequence = dataParts[dataParts.length - 2];
          const prefix = dataParts[dataParts.length - 3];

          // Extract batchId from the URL using code parts + number of dashes (3)
          const batchId = codeParts[1].slice(
            0,
            -(checkSum.length + codeSequence.length + prefix.length + 3),
          );
          code = `${codeParts[0]}.${batchId}.${prefix}-${codeSequence}-${checkSum}`;
        }
      }

      store.dispatch('code/setCode', code);
    },
    check() {
      this.dispatch();
    },
  },
};
</script>

<style lang="scss" scoped>
.public-container {
  max-width: 800px;
  margin: 0 auto;
  background-color: white;
  padding-bottom: 45px;
  background-size: cover !important;
  background-repeat: no-repeat;
  background-position: center;
}
</style>
